import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { WorkflowBuilderStoreService } from '../../services/workflow-builder-store.service';
import {
  IAction,
  IAuthorisation,
  IBuilderModel,
  IGuardClause,
  IStateMeta,
  ITransition,
} from '../../models/workflow-builder.model';
import {
  ACTION_BY_TYPE,
  ACTION_META,
  COMPARATORS,
  DEFAULT_STATE_COLOUR,
} from '../../core/catalogs';
import { ActionArgsFormComponent } from '../action-args-form/action-args-form.component';
import { NotificationActionEditorComponent } from '../notification-action-editor/notification-action-editor.component';
import { NotificationVariable } from '../../core/notification-editor';
import { IServiceLanguage } from '../../../../../core/models/service-language.model';
import { ServiceChangeRequestService } from '../../../../../core/services/service-change-request.service';

export type InspectorMode = 'state' | 'event' | 'condition' | 'action';
export interface InspectorTarget {
  mode: InspectorMode;
  tid: string;
  which?: 'one' | 'two';
  stateName?: string;
  actionIdx?: number;
}

export interface InspectorActionDeleteRequest {
  tid: string;
  which: 'one' | 'two';
  index: number;
  actionType: string;
}

interface InspectorActionItem {
  action: IAction;
  index: number;
}

interface LocaleField {
  key: string;
  label: string;
}

interface IntegrationCallbackPrompt {
  mode: 'add' | 'save';
  tid: string;
  which: 'one' | 'two';
  currentStateCode: string;
  actionType?: string;
  required?: boolean;
  actionIndex?: number;
  args?: Record<string, unknown> | null;
  newActionIndex?: number;
  afterConfirm?: 'close' | 'state' | null;
}

const NOTIFICATION_ACTION_TYPES = new Set([
  'NOTIFICATION',
  'INTERNAL_NOTIFICATION',
  'OFFICE_ASSIGNMENT_NOTIFICATION',
  'FORM_FIELD_NOTIFICATION',
  'RESEND_NOTIFICATION',
]);

/** Right-side inspector drawer. Reads/writes the selected transition via the shared store. */
@Component({
  selector: 'irembogov-iwb-inspector',
  templateUrl: './inspector.component.html',
})
export class InspectorComponent implements OnChanges, OnDestroy {
  @Input() target!: InspectorTarget;
  @Input() organizationId?: string;
  @Input() formFields?: unknown;
  /** Backend application-state catalog (code + label + colour) for the state-code picker. */
  @Input() apiStates: IStateMeta[] = [];
  @Output() closed = new EventEmitter<void>();
  /** From the state view's action list → open that single action's editor. */
  @Output() actionEdit = new EventEmitter<number>();
  /** "Copy event" → copies the event config to the clipboard; emits the event name (for a toast). */
  @Output() eventCopied = new EventEmitter<string>();
  /** "Delete event" → asks the builder to remove this transition (it confirms first). */
  @Output() deleteEventRequest = new EventEmitter<string>();
  /** "Delete state" → asks the builder to remove this state (it confirms first). */
  @Output() deleteStateRequest = new EventEmitter<string>();
  /** "Delete action" → asks the builder to remove this action (it confirms first). */
  @Output() deleteActionRequest =
    new EventEmitter<InspectorActionDeleteRequest>();
  /** Single action editor → return to the whole state editor without closing the drawer. */
  @Output() stateEditRequest = new EventEmitter<void>();

  @ViewChildren(ActionArgsFormComponent)
  argForms?: QueryList<ActionArgsFormComponent>;
  @ViewChild(NotificationActionEditorComponent)
  notificationEditor?: NotificationActionEditorComponent;

  t: ITransition | undefined;
  stateNameDraft = '';
  stateCodeDraft = '';
  stateNamesDraft: Record<string, string> = {};
  selectedStateNameLocale = 'en-US';
  selectedEventNameLocale = 'en-US';
  stateNamesExpanded = false;
  eventNamesExpanded = false;
  languages: IServiceLanguage[] = [];
  readonly comparators = COMPARATORS;
  readonly actionPalette = ACTION_META;
  readonly minDrawerWidth = 420;
  readonly maxDrawerWidth = 960;
  readonly defaultStateColour = DEFAULT_STATE_COLOUR;
  drawerWidth = 520;
  integrationCallbackPrompt: IntegrationCallbackPrompt | null = null;
  integrationCallbackStateDraft = '';
  integrationCallbackSearch = '';
  integrationCallbackError = '';

  private sub = new Subscription();
  private latest: IBuilderModel = { transitions: [], states: [], topRaw: {} };
  private stateDraftTargetKey = '';
  private stateDraftPreviousCode = '';
  private readonly drawerWidthStorageKey = 'iwb-v2-inspector-width';

  constructor(
    private store: WorkflowBuilderStoreService,
    private cdr: ChangeDetectorRef,
    private readonly serviceChangeRequestService: ServiceChangeRequestService
  ) {
    this.drawerWidth = this.readDrawerWidth();
    this.serviceChangeRequestService.ensureServiceLanguagesLoaded();
    this.sub.add(
      this.store.model$.subscribe(m => {
        this.latest = m;
        this.t = m.transitions.find(x => x.id === this.target?.tid);
        this.resetStateDraftIfNeeded();
        this.cdr.markForCheck();
      })
    );
    this.sub.add(
      this.serviceChangeRequestService.languages.subscribe(languages => {
        const activeLanguages = languages.filter(
          language => language.status !== 'INACTIVE'
        );
        this.languages = activeLanguages.length
          ? activeLanguages
          : [this.serviceChangeRequestService.DefaultLanguage];
        this.ensureSelectedLanguageLocales();
        this.cdr.markForCheck();
      })
    );
  }
  ngOnChanges(): void {
    // @Input target binds after the constructor subscription fires, so re-resolve here.
    this.t = this.latest.transitions.find(x => x.id === this.target?.tid);
    this.resetStateDraftIfNeeded();
    // A newly-inspected state opens on its Properties tab.
    this.stateTab = 'properties';
  }
  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  get endState() {
    return this.t
      ? this.target.which === 'two'
        ? this.t.two
        : this.t.one
      : null;
  }
  get actions(): IAction[] {
    return this.endState ? this.endState.actions : [];
  }
  get breakingActionItems(): InspectorActionItem[] {
    return this.indexedActions.filter(item => !!item.action.required);
  }
  get nonBreakingActionItems(): InspectorActionItem[] {
    return this.indexedActions.filter(item => !item.action.required);
  }
  get actionPickerOptions(): NotificationVariable[] {
    return this.actionPalette.map(action => {
      const group = [
        'NOTIFICATION',
        'INTERNAL_NOTIFICATION',
        'OFFICE_ASSIGNMENT_NOTIFICATION',
        'FORM_FIELD_NOTIFICATION',
        'RESEND_NOTIFICATION',
      ].includes(action.type)
        ? 'Notifications'
        : ['INTEGRATION', 'URL_CALL', 'FETCH_ID_PHOTO'].includes(action.type)
        ? 'Integrations'
        : action.type.includes('CERTIFICATE')
        ? 'Documents'
        : 'Workflow actions';
      return {
        group,
        label: action.plain,
        value: action.type,
      };
    });
  }

  private get indexedActions(): InspectorActionItem[] {
    return this.actions.map((action, index) => ({ action, index }));
  }

  // ── nextEvent: the event the SYSTEM runs automatically after landing on this state ──
  /** Current nextEvent on the edited end state ('' when none). */
  get nextEventValue(): string {
    return this.endState?.nextEvent ?? '';
  }
  private _nextEvtKey = '';
  private _nextEvtOpts: string[] = [];
  /** nextEvent choices: ANY event in the workflow (plus the current value if it's not listed).
   *  Memoised to a STABLE reference so the <select> options aren't recreated every CD. */
  get nextEventOptions(): string[] {
    const evs = new Set<string>();
    for (const t of this.latest.transitions) evs.add(t.event);
    const cur = this.endState?.nextEvent;
    if (cur) evs.add(cur); // keep the current value visible even if it's not (yet) a real event
    const list = [...evs].sort((a, b) => a.localeCompare(b));
    const key = list.join('|');
    if (key !== this._nextEvtKey) {
      this._nextEvtKey = key;
      this._nextEvtOpts = list;
    }
    return this._nextEvtOpts;
  }
  setNextEvent(value: string): void {
    if (!this.target?.tid) return;
    this.store.setNextEvent(
      this.target.tid,
      this.target.which || 'one',
      value || null
    );
  }
  /** The single action being edited in 'action' mode. */
  get currentAction(): IAction | null {
    if (this.target?.mode !== 'action' || this.target.actionIdx == null)
      return null;
    return this.endState?.actions[this.target.actionIdx] ?? null;
  }
  meta(a: IAction) {
    return (
      ACTION_BY_TYPE[a.actionType] || {
        type: a.actionType,
        plain: a.actionType,
        icon: '⚙',
        cls: 'sys',
      }
    );
  }

  isNotificationAction(action: IAction | null | undefined): boolean {
    return NOTIFICATION_ACTION_TYPES.has(
      action?.actionType?.trim().toUpperCase() || ''
    );
  }

  isCertificateReminderAction(action: IAction | null | undefined): boolean {
    return action?.actionType?.trim().toUpperCase() === 'GENERATE_CERTIFICATE';
  }

  /** Stable identity per action card so store echoes don't destroy/recreate (and close) open overlays. */
  trackByActionIndex(i: number): number {
    return i;
  }
  /** Stable identity per clause row so store echoes don't destroy/recreate the focused input. */
  trackByClauseIndex(i: number): number {
    return i;
  }
  trackByStateCode(_: number, state: IStateMeta): string {
    return state.code;
  }

  trackByLocale(_: number, locale: LocaleField): string {
    return locale.key;
  }

  /**
   * Flush every action card's locally-edited args into the store in one pass.
   * We snapshot first (the setActionArgs calls re-render the cards) and only
   * persist here — never on each keystroke — so edits cause no live store churn
   * (which was tearing down open dropdowns / overlays mid-interaction).
   */
  private commitActionArgs(
    afterConfirm: 'close' | 'state' | null = null
  ): boolean {
    // Only the single-action editor hosts a live form now; commit it to its index.
    if (
      this.target?.mode !== 'action' ||
      !this.argForms ||
      this.target.actionIdx == null
    )
      return true;
    const formEditor = this.argForms?.first;
    const notificationEditor = this.notificationEditor;
    const formArgs = formEditor?.currentArgs();
    const notificationArgs = notificationEditor?.currentArgs();
    if (formEditor && formArgs === undefined) return false;
    if (notificationEditor && notificationArgs === undefined) return false;
    const args = {
      ...((formArgs || {}) as Record<string, unknown>),
      ...((notificationArgs || {}) as Record<string, unknown>),
    };
    const action = this.currentAction;
    const currentStateCode = this.endState?.stateCode ?? '';
    if (
      action &&
      this.needsIntegrationCallback(action, args) &&
      currentStateCode
    ) {
      const callbackStateCode =
        this.integrationCallbackStateFor(currentStateCode);
      if (!callbackStateCode) {
        this.openIntegrationCallbackPrompt({
          mode: 'save',
          tid: this.target.tid,
          which: this.target.which || 'one',
          currentStateCode,
          actionIndex: this.target.actionIdx,
          args,
          afterConfirm,
        });
        return false;
      }
      this.store.setActionArgs(
        this.target.tid,
        this.target.which || 'one',
        this.target.actionIdx,
        args,
        callbackStateCode
      );
      return true;
    }
    this.store.setActionArgs(
      this.target.tid,
      this.target.which || 'one',
      this.target.actionIdx,
      args
    );
    return true;
  }

  get maxDrawerResizeWidth(): number {
    return typeof window === 'undefined'
      ? this.maxDrawerWidth
      : Math.min(
          this.maxDrawerWidth,
          Math.max(this.minDrawerWidth, window.innerWidth - 48)
        );
  }

  onDrawerResizeCommitted(width: number): void {
    this.drawerWidth = this.clampDrawerWidth(width);
    this.persistDrawerWidth();
    this.cdr.markForCheck();
  }

  private readDrawerWidth(): number {
    if (typeof localStorage === 'undefined') return this.drawerWidth;
    const saved = Number(localStorage.getItem(this.drawerWidthStorageKey));
    return Number.isFinite(saved) && saved > 0
      ? this.clampDrawerWidth(saved)
      : this.drawerWidth;
  }

  private persistDrawerWidth(): void {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(this.drawerWidthStorageKey, String(this.drawerWidth));
  }

  private clampDrawerWidth(width: number): number {
    return Math.min(
      Math.max(width, this.minDrawerWidth),
      this.maxDrawerResizeWidth
    );
  }

  private stateTargetKey(): string {
    if (this.target?.mode !== 'state') return '';
    return `${this.target.tid}:${this.target.which || 'one'}`;
  }

  private resetStateDraftIfNeeded(): void {
    const key = this.stateTargetKey();
    if (!key || key === this.stateDraftTargetKey) return;
    const es = this.endState;
    this.stateDraftTargetKey = key;
    this.stateDraftPreviousCode = es?.stateCode ?? '';
    this.stateCodeDraft = es?.stateCode ?? '';
    this.stateNameDraft =
      ((es?.raw as any)?.stateName as string) ||
      this.target.stateName ||
      es?.stateCode ||
      '';
    this.stateNamesDraft = this.readStateNames(es?.raw);
  }

  private commitStateIdentity(): void {
    if (this.target?.mode !== 'state' || !this.stateDraftPreviousCode) return;
    const stateCode = this.stateCodeDraft.trim();
    if (!stateCode) {
      this.stateCodeDraft = this.stateDraftPreviousCode;
      return;
    }
    const stateName = this.stateNameDraft.trim() || stateCode;
    const currentName =
      ((this.endState?.raw as any)?.stateName as string) ||
      this.target.stateName ||
      this.endState?.stateCode ||
      '';
    const stateNames = this.normalizedStateNames();
    const currentStateNames = this.normalizedStateNames(
      this.readStateNames(this.endState?.raw)
    );
    const stateNamesUnchanged =
      JSON.stringify(stateNames ?? null) ===
      JSON.stringify(currentStateNames ?? null);
    if (
      stateCode === this.stateDraftPreviousCode &&
      stateName === currentName &&
      stateNamesUnchanged
    )
      return;

    this.store.setStateIdentity(this.stateDraftPreviousCode, {
      stateCode,
      stateName,
      stateNames,
    });
    this.stateDraftPreviousCode = stateCode;
  }

  get localeFields(): LocaleField[] {
    const languages = this.languages.length
      ? this.languages
      : [this.serviceChangeRequestService.DefaultLanguage];
    return languages.map(language => ({
      key: language.locale,
      label: language.name || language.nativeName || language.locale,
    }));
  }

  stateLocalizedName(locale: string): string {
    return this.valueForLocale(this.stateNamesDraft, locale);
  }

  setStateLocalizedName(locale: string, value: string): void {
    const names = { ...this.stateNamesDraft };
    delete names[this.shortLocaleKey(locale)];
    if (value) names[locale] = value;
    else delete names[locale];
    this.stateNamesDraft = names;
  }

  selectStateNameLocale(locale: string): void {
    this.selectedStateNameLocale = locale;
  }

  selectEventNameLocale(locale: string): void {
    this.selectedEventNameLocale = locale;
  }

  toggleStateNamesExpanded(): void {
    this.stateNamesExpanded = !this.stateNamesExpanded;
  }

  toggleEventNamesExpanded(): void {
    this.eventNamesExpanded = !this.eventNamesExpanded;
  }

  missingStateNameLanguages(): IServiceLanguage[] {
    return this.missingLocalizationLanguages(this.stateNamesDraft);
  }

  configuredStateNameLanguages(): IServiceLanguage[] {
    return this.configuredLocalizationLanguages(this.stateNamesDraft);
  }

  missingEventNameLanguages(): IServiceLanguage[] {
    return this.missingLocalizationLanguages(this.t?.eventNames || {});
  }

  configuredEventNameLanguages(): IServiceLanguage[] {
    return this.configuredLocalizationLanguages(this.t?.eventNames || {});
  }

  missingLanguageLabels(languages: IServiceLanguage[]): string {
    return languages
      .map(
        language => `${language.name || language.locale} (${language.locale})`
      )
      .join(', ');
  }

  private readStateNames(raw: unknown): Record<string, string> {
    const names =
      raw && typeof raw === 'object' && !Array.isArray(raw)
        ? (raw as Record<string, unknown>)['stateNames']
        : null;
    if (!names || typeof names !== 'object' || Array.isArray(names)) return {};
    return Object.entries(names as Record<string, unknown>).reduce(
      (acc, [key, value]) => {
        const label = String(value ?? '').trim();
        if (label) acc[key] = label;
        return acc;
      },
      {} as Record<string, string>
    );
  }

  private normalizedStateNames(
    source: Record<string, string> = this.stateNamesDraft
  ): Record<string, string> | null {
    const names = Object.entries(source).reduce((acc, [key, value]) => {
      const label = String(value ?? '').trim();
      if (label) acc[key] = label;
      return acc;
    }, {} as Record<string, string>);
    if (
      !names['en-US'] &&
      !names['en'] &&
      this.stateNameDraft.trim() &&
      this.stateNameDraft.trim() !== this.stateCodeDraft.trim()
    ) {
      names['en-US'] = this.stateNameDraft.trim();
    }
    return Object.keys(names).length ? names : null;
  }

  // ── state-code picker (same searchable BE-state selector as the add-event composer) ──
  private _stateOptBase: IStateMeta[] | null = null;
  private _stateOptions: IStateMeta[] = [];
  /**
   * Memoised, STABLE-reference option list for the state-code picker (BE catalog, else the
   * workflow's own states). A fresh array each change-detection would make ng-select rebuild
   * its options mid-click under OnPush and drop the selection — so recompute only on change.
   */
  get stateOptions(): IStateMeta[] {
    const base = this.apiStates.length ? this.apiStates : this.latest.states;
    if (base !== this._stateOptBase) {
      this._stateOptBase = base;
      this._stateOptions = [...base];
    }
    return this._stateOptions;
  }

  get integrationCompletionStateOptions(): IStateMeta[] {
    const current =
      this.integrationCallbackPrompt?.currentStateCode ||
      this.endState?.stateCode ||
      '';
    return this.stateOptions.filter(option => option.code !== current);
  }

  get filteredIntegrationCompletionStateOptions(): IStateMeta[] {
    const term = this.integrationCallbackSearch.trim().toLowerCase();
    const options = this.integrationCompletionStateOptions;
    if (!term) return options;
    return options.filter(
      option =>
        option.code.toLowerCase().includes(term) ||
        option.name.toLowerCase().includes(term)
    );
  }

  get canConfirmIntegrationCallbackState(): boolean {
    const picked = this.integrationCallbackStateDraft.trim();
    return (
      !!picked &&
      !!this.integrationCallbackPrompt &&
      picked !== this.integrationCallbackPrompt.currentStateCode
    );
  }

  /** ng-select filter: match the typed term against either the state code OR its name. */
  searchState = (term: string, item: IStateMeta): boolean => {
    const t = (term || '').toLowerCase();
    return (
      (item.code || '').toLowerCase().includes(t) ||
      (item.name || '').toLowerCase().includes(t)
    );
  };

  /** Allow typing a brand-new code that isn't in the catalog (custom state). */
  addStateCodeTag = (term: string): IStateMeta => {
    const code = (term || '')
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
    return { code, name: code, colour: DEFAULT_STATE_COLOUR };
  };

  useCustomIntegrationCallbackState(): void {
    const custom = this.addStateCodeTag(this.integrationCallbackSearch);
    if (!custom.code) return;
    this.onIntegrationCallbackStatePicked(custom);
  }

  /** When a catalog state is picked, adopt its human name (unless the user typed a custom one). */
  onStateCodePicked(picked: IStateMeta | string | null | undefined): void {
    const item =
      typeof picked === 'string'
        ? this.stateOptions.find(s => s.code === picked)
        : picked;
    if (!item || !item.name || item.name === item.code) return;
    const prev = this.stateNameDraft.trim();
    const uncustomised =
      !prev ||
      prev === this.stateDraftPreviousCode ||
      prev === this.stateCodeDraft;
    if (uncustomised) this.stateNameDraft = item.name;
    if (
      !this.stateNamesDraft['en-US'] &&
      item.name &&
      item.name !== item.code
    ) {
      this.setStateLocalizedName('en-US', item.name);
    }
  }

  // structural ops commit pending arg edits first so nothing typed is lost
  toggleReq(i: number): void {
    if (!this.commitActionArgs()) return;
    this.store.toggleRequired(this.target.tid, this.target.which || 'one', i);
  }
  removeAction(i: number): void {
    if (!this.commitActionArgs()) return;
    this.store.removeAction(this.target.tid, this.target.which || 'one', i);
  }
  requestActionDelete(i: number): void {
    if (!this.commitActionArgs()) return;
    const action = this.actions[i];
    if (!action) return;
    this.deleteActionRequest.emit({
      tid: this.target.tid,
      which: this.target.which || 'one',
      index: i,
      actionType: this.meta(action).plain || action.actionType,
    });
  }
  backToState(): void {
    const committed = this.commitActionArgs('state');
    if (!committed && this.integrationCallbackPrompt) return;
    this.stateEditRequest.emit();
  }
  pickAction(type: string, required: boolean): void {
    if (!this.commitActionArgs()) return;
    if (type === 'INTEGRATION') {
      const currentStateCode = this.endState?.stateCode ?? '';
      if (!currentStateCode) return;
      this.openIntegrationCallbackPrompt({
        mode: 'add',
        tid: this.target.tid,
        which: this.target.which || 'one',
        currentStateCode,
        actionType: type,
        required,
        newActionIndex: this.actions.length,
      });
      return;
    }
    const newIndex = this.actions.length;
    this.store.addAction(
      this.target.tid,
      this.target.which || 'one',
      type,
      required
    );
    this.actionEdit.emit(newIndex);
  }

  cancelIntegrationCallbackState(): void {
    this.integrationCallbackPrompt = null;
    this.integrationCallbackStateDraft = '';
    this.integrationCallbackSearch = '';
    this.integrationCallbackError = '';
  }

  onIntegrationCallbackStatePicked(
    picked: IStateMeta | string | null | undefined
  ): void {
    const stateCode = typeof picked === 'string' ? picked : picked?.code;
    if (!stateCode) return;
    this.integrationCallbackStateDraft = stateCode;
    this.integrationCallbackError = '';
    this.confirmIntegrationCallbackState();
  }

  confirmIntegrationCallbackState(): void {
    const prompt = this.integrationCallbackPrompt;
    const stateCode = this.integrationCallbackStateDraft.trim();
    if (!prompt) return;
    if (!stateCode || stateCode === prompt.currentStateCode) {
      this.integrationCallbackError =
        'Choose a completion state different from the integration state.';
      return;
    }

    this.integrationCallbackPrompt = null;
    this.integrationCallbackStateDraft = '';
    this.integrationCallbackSearch = '';
    this.integrationCallbackError = '';

    if (prompt.mode === 'add') {
      this.store.addAction(
        prompt.tid,
        prompt.which,
        prompt.actionType || 'INTEGRATION',
        !!prompt.required,
        stateCode
      );
      this.actionEdit.emit(prompt.newActionIndex ?? this.actions.length);
      return;
    }

    if (prompt.actionIndex == null) return;
    this.store.setActionArgs(
      prompt.tid,
      prompt.which,
      prompt.actionIndex,
      prompt.args ?? null,
      stateCode
    );
    if (prompt.afterConfirm === 'close') {
      this.commitStateIdentity();
      this.closed.emit();
    } else if (prompt.afterConfirm === 'state') {
      this.stateEditRequest.emit();
    }
  }

  // ── event ──
  get isSystem(): boolean {
    return !!this.t?.authorisation?.systemAuthorised;
  }
  get roles(): string {
    return this.t?.authorisation?.authorisedRoles?.join(', ') || '';
  }
  eventName(locale: string): string {
    const names = this.t?.eventNames || {};
    return names[locale] || names[this.shortLocaleKey(locale)] || '';
  }
  setSystem(checked: boolean): void {
    const auth: IAuthorisation = checked
      ? {
          systemAuthorised: true,
          authorisedRoles: null,
          authorisedUsers: this.t?.authorisation?.authorisedUsers ?? null,
        }
      : {
          systemAuthorised: false,
          authorisedRoles: this.t?.authorisation?.authorisedRoles ?? [],
          authorisedUsers: this.t?.authorisation?.authorisedUsers ?? null,
        };
    this.store.setAuthorisation(this.target.tid, auth);
  }
  setRoles(value: string): void {
    const list = value
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);
    this.store.setAuthorisation(this.target.tid, {
      systemAuthorised: false,
      authorisedRoles: list,
      authorisedUsers: this.t?.authorisation?.authorisedUsers ?? null,
    });
  }
  setEventName(locale: string, value: string): void {
    const names: Record<string, string> = Object.entries(
      this.t?.eventNames ?? {}
    ).reduce((acc, [key, item]) => {
      if (item) acc[key] = item;
      return acc;
    }, {} as Record<string, string>);
    const legacyKey = this.shortLocaleKey(locale);
    delete names[legacyKey];
    if (value) names[locale] = value;
    else delete names[locale];
    this.store.setEventNames(
      this.target.tid,
      (Object.keys(names).length ? names : null) as any
    );
  }

  private shortLocaleKey(locale: string): string {
    return locale.split('-')[0] || locale;
  }

  private ensureSelectedLanguageLocales(): void {
    const locales = this.localeFields.map(locale => locale.key);
    const fallback = locales[0] || 'en-US';
    if (!locales.includes(this.selectedStateNameLocale)) {
      this.selectedStateNameLocale = fallback;
    }
    if (!locales.includes(this.selectedEventNameLocale)) {
      this.selectedEventNameLocale = fallback;
    }
  }

  private valueForLocale(
    values: Record<string, unknown> | null | undefined,
    locale: string
  ): string {
    if (!values) return '';
    return String(
      values[locale] ?? values[this.shortLocaleKey(locale)] ?? ''
    ).trim();
  }

  private missingLocalizationLanguages(
    values: Record<string, unknown> | null | undefined
  ): IServiceLanguage[] {
    const languages = this.languages.length
      ? this.languages
      : [this.serviceChangeRequestService.DefaultLanguage];
    const hasAnyConfiguredLanguage = languages.some(language =>
      this.valueForLocale(values, language.locale)
    );
    if (!hasAnyConfiguredLanguage) return [];
    return languages.filter(
      language => !this.valueForLocale(values, language.locale)
    );
  }

  private configuredLocalizationLanguages(
    values: Record<string, unknown> | null | undefined
  ): IServiceLanguage[] {
    const languages = this.languages.length
      ? this.languages
      : [this.serviceChangeRequestService.DefaultLanguage];
    return languages.filter(language =>
      this.valueForLocale(values, language.locale)
    );
  }

  // ── add a condition (split this event into two outcomes) ──
  get hasCondition(): boolean {
    return !!this.t?.two || !!this.t?.guard;
  }
  get outcomeOneCode(): string {
    return this.t?.one?.stateCode ?? '—';
  }
  get outcomeTwoCode(): string {
    return this.t?.two?.stateCode ?? '—';
  }
  addCondition(): void {
    this.store.addCondition(this.target.tid);
  }
  setOutcomeState(
    which: 'one' | 'two',
    picked: IStateMeta | string | null | undefined
  ): void {
    const code = typeof picked === 'string' ? picked : picked?.code;
    if (code) this.store.setEndStateCode(this.target.tid, which, code);
  }

  /** Copy this event's full config to the clipboard, for pasting into a new event (⌘V). */
  onCopyEvent(): void {
    if (this.store.copyEvent(this.target.tid))
      this.eventCopied.emit(this.t?.event || '');
  }

  // ── condition ──
  get clauses(): IGuardClause[] {
    return this.t?.guard?.clauses ?? [];
  }
  setClause(
    i: number,
    field: 'parameter' | 'comparator' | 'value',
    value: string
  ): void {
    const next = this.clauses.map((c, idx) =>
      idx === i ? { ...c, [field]: value } : c
    );
    this.store.setGuardClauses(this.target.tid, next);
  }
  addClause(): void {
    this.store.setGuardClauses(this.target.tid, [
      ...this.clauses,
      { parameter: '', comparator: 'EQ', value: '' },
    ]);
  }
  removeClause(i: number): void {
    const next = this.clauses.filter((_, idx) => idx !== i);
    if (next.length) this.store.setGuardClauses(this.target.tid, next);
    else this.store.removeGuard(this.target.tid);
  }
  removeCondition(): void {
    this.store.removeCondition(this.target.tid);
  }

  // ── per-state editor: tabbed (Properties | JSON), like the form v2 field editor ──
  stateTab: 'properties' | 'json' = 'properties';
  showStateJson = false;
  jsonDraft = '';
  jsonError = '';

  /** Switch the state inspector tab; entering JSON loads the whole transition. */
  selectStateTab(tab: 'properties' | 'json'): void {
    this.stateTab = tab;
    if (tab === 'json') {
      // The WHOLE transition (event -> authorisation -> endStates -> everything).
      this.jsonDraft = JSON.stringify(
        this.store.transitionJson(this.target.tid),
        null,
        2
      );
      this.jsonError = '';
    }
  }
  // Monaco options — same look as the form builder's per-field JSON editor.
  jsonEditorOptions = {
    theme: 'vs',
    language: 'json',
    minimap: { enabled: false },
    lineNumbers: 'on' as const,
    folding: true,
    automaticLayout: true,
    scrollBeyondLastLine: false,
    wordWrap: 'on' as const,
  };
  toggleStateJson(): void {
    this.showStateJson = !this.showStateJson;
    if (this.showStateJson) {
      // Show the WHOLE transition (event → authorisation → endStates → everything), not just this endState.
      this.jsonDraft = JSON.stringify(
        this.store.transitionJson(this.target.tid),
        null,
        2
      );
      this.jsonError = '';
    }
  }
  applyStateJson(): void {
    try {
      const node = JSON.parse(this.jsonDraft);
      this.store.replaceTransition(this.target.tid, node);
      this.stateDraftTargetKey = '';
      this.jsonError = '';
      this.showStateJson = false;
      this.stateTab = 'properties';
    } catch {
      this.jsonError = 'Invalid JSON — fix and try again.';
    }
  }

  /** Dismiss the drawer. Valid action edits are saved; invalid ones are left unchanged. */
  close(): void {
    const committed = this.commitActionArgs('close');
    if (!committed && this.integrationCallbackPrompt) return;
    this.commitStateIdentity();
    this.closed.emit();
  }

  /** Explicit save path: keep the drawer open when the embedded editor reports invalid args. */
  saveAndClose(): void {
    if (!this.commitActionArgs('close')) return;
    this.commitStateIdentity();
    this.closed.emit();
  }

  /** Ask the builder to delete this event (it shows a confirmation first). */
  deleteEvent(): void {
    if (!this.target?.tid) return;
    this.deleteEventRequest.emit(this.target.tid);
  }

  /** Ask the builder to delete this state (it shows a confirmation first). */
  deleteState(): void {
    const code = this.endState?.stateCode;
    if (!code) return;
    this.deleteStateRequest.emit(code);
  }

  private openIntegrationCallbackPrompt(
    prompt: IntegrationCallbackPrompt
  ): void {
    this.integrationCallbackPrompt = prompt;
    this.integrationCallbackStateDraft = '';
    this.integrationCallbackSearch = '';
    this.integrationCallbackError = '';
    this.cdr.markForCheck();
  }

  private needsIntegrationCallback(
    action: IAction,
    args: Record<string, unknown> | null
  ): boolean {
    return action.actionType === 'INTEGRATION' && !!args?.['async'];
  }

  private integrationCallbackStateFor(stateCode: string): string | null {
    const callback = this.latest.transitions.find(
      transition =>
        transition.startState === stateCode && transition.event === 'PUSH'
    );
    const callbackStateCode = callback?.one?.stateCode;
    return callbackStateCode && callbackStateCode !== stateCode
      ? callbackStateCode
      : null;
  }
}
